import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';

// All 99 languages supported by Whisper large-v3-turbo
const ROW_1 = [
  'English', 'German', 'French', 'Spanish', 'Italian', 'Portuguese', 'Dutch',
  'Polish', 'Czech', 'Slovak', 'Romanian', 'Hungarian', 'Greek', 'Swedish',
  'Norwegian', 'Danish', 'Finnish', 'Estonian', 'Latvian', 'Lithuanian',
  'Slovenian', 'Croatian', 'Serbian', 'Bulgarian', 'Macedonian', 'Albanian',
  'Catalan', 'Galician', 'Basque', 'Welsh', 'Icelandic', 'Faroese',
  'Luxembourgish', 'Breton', 'Occitan', 'Latin', 'Maltese', 'Afrikaans',
  'Swahili', 'Hausa', 'Yoruba', 'Somali', 'Shona', 'Lingala',
  'Haitian Creole', 'Malagasy',
];

const ROW_2 = [
  'Russian', 'Ukrainian', 'Belarusian', 'Georgian', 'Armenian', 'Azerbaijani',
  'Kazakh', 'Uzbek', 'Turkmen', 'Tajik', 'Tatar', 'Bashkir', 'Mongolian',
  'Arabic', 'Hebrew', 'Persian', 'Pashto', 'Urdu', 'Hindi', 'Bengali',
  'Gujarati', 'Marathi', 'Punjabi', 'Assamese', 'Sindhi', 'Nepali',
  'Sinhala', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Thai', 'Burmese',
  'Khmer', 'Lao', 'Tibetan', 'Japanese', 'Chinese', 'Korean', 'Vietnamese',
  'Indonesian', 'Malay', 'Tagalog', 'Javanese', 'Sundanese', 'Turkish',
  'Māori', 'Hawaiian', 'Sanskrit', 'Yiddish', 'Amharic',
];

const Track = ({ items, direction, speed }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const animName = direction === 'left' ? 'marquee-left' : 'marquee-right';

  return (
    <Box
      sx={{
        overflow: 'hidden',
        width: '100%',
        maskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
        WebkitMaskImage: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)',
        [`@keyframes marquee-left`]: {
          '0%':   { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(-50%)' },
        },
        [`@keyframes marquee-right`]: {
          '0%':   { transform: 'translateX(-50%)' },
          '100%': { transform: 'translateX(0)' },
        },
        '&:hover .marquee-track': {
          animationPlayState: 'paused',
        },
      }}
    >
      <Box
        className="marquee-track"
        sx={{
          display: 'inline-flex',
          whiteSpace: 'nowrap',
          animation: `${animName} ${speed}s linear infinite`,
          gap: 1,
          py: 0.5,
        }}
      >
        {/* Duplicate items for seamless loop */}
        {[...items, ...items].map((lang, i) => (
          <Box
            key={i}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: '6px',
              border: '1px solid',
              borderColor: isDark ? 'grey.700' : 'grey.300',
              bgcolor: isDark ? 'grey.900' : 'grey.50',
              color: 'text.secondary',
              fontSize: '0.78rem',
              fontWeight: 500,
              letterSpacing: '0.01em',
              userSelect: 'none',
              flexShrink: 0,
              transition: 'border-color 0.15s, color 0.15s',
              '&:hover': {
                borderColor: 'primary.main',
                color: 'primary.main',
              },
            }}
          >
            {lang}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const LanguageMarquee = () => (
  <Box sx={{ py: { xs: 6, md: 8 }, overflow: 'hidden' }}>
    <Box sx={{ textAlign: 'center', mb: 4 }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.disabled', letterSpacing: '0.15em', fontSize: '0.7rem' }}
      >
        99 supported languages
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 700, mt: 0.5, fontSize: { xs: '1.2rem', md: '1.5rem' } }}
      >
        Just upload. We handle the language.
      </Typography>
    </Box>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Track items={ROW_1} direction="left"  speed={55} />
      <Track items={ROW_2} direction="right" speed={50} />
    </Box>
  </Box>
);

export default LanguageMarquee;
